import { useCallback, useEffect, useRef, useState } from 'react';
import { useAzureFileUpload } from '@repo/file-uploader';
import { TemplateType, useLazyPostTemplateGetUploadQuery, useLazyPostTemplateUploadCompleteQuery } from '@/api';

export type TemplateUploadStatus = 'idle' | 'preparing' | 'uploading' | 'completing' | 'success' | 'error';

/**
 * Drives the three-step custom-template upload documented in the endpoint
 * guide:
 *   1. POST template/get-upload      → SAS `uploadPath` + `uploadToken` + id
 *   2. PUT  <blob>                    → file bytes (via the Azure uploader)
 *   3. POST template/upload-complete  → persists the template as "uploaded"
 *
 * Exposes a single `uploadTemplate(templateType, file)` promise plus coarse
 * status so the section can show progress and toast the outcome.
 *
 * The Azure manager only calls `onUploadComplete` on *success*; on failure it
 * flips the upload item's status to 'error' / 'cancelled'. So we resolve the
 * per-file promise from `onUploadComplete` and reject it by watching the
 * upload list for a terminal error on the tracked id — otherwise a failed blob
 * PUT would leave the flow stuck on "uploading" forever.
 */
export const useUploadTemplate = (onUploaded?: () => void) => {
  const [status, setStatus] = useState<TemplateUploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const [triggerGetUpload] = useLazyPostTemplateGetUploadQuery();
  const [triggerUploadComplete] = useLazyPostTemplateUploadCompleteQuery();

  // Pending-promise handlers keyed by the in-flight upload id, resolved from
  // the manager's success callback and rejected from the list watcher below.
  const resolversRef = useRef(new Map<string, () => void>());
  const rejectersRef = useRef(new Map<string, (reason?: unknown) => void>());

  const settle = useCallback((id: string, ok: boolean, reason?: unknown) => {
    const resolve = resolversRef.current.get(id);
    const reject = rejectersRef.current.get(id);
    resolversRef.current.delete(id);
    rejectersRef.current.delete(id);
    if (ok) resolve?.();
    else reject?.(reason instanceof Error ? reason : new Error('Upload failed'));
  }, []);

  const { uploadFile, deleteUpload, uploadList } = useAzureFileUpload({
    onUploadComplete: (id: string) => {
      deleteUpload(id);
      settle(id, true);
    },
  });

  // Reject the pending promise as soon as a tracked upload lands in a terminal
  // error / cancelled state (blob PUT rejected, aborted, invalid SAS, etc.).
  useEffect(() => {
    uploadList.forEach((item) => {
      if ((item.status === 'error' || item.status === 'cancelled') && rejectersRef.current.has(item.id)) {
        settle(item.id, false, item.error ?? new Error(`Upload ${item.status}`));
        deleteUpload(item.id);
      }
    });
  }, [uploadList, settle, deleteUpload]);

  const uploadTemplate = useCallback(
    async (templateType: TemplateType, file: File): Promise<void> => {
      setError(null);
      setStatus('preparing');
      try {
        const { uploadPath, uploadToken, templateId } = await triggerGetUpload({
          templateType,
          documentName: file.name,
        }).unwrap();

        setStatus('uploading');
        await new Promise<void>((resolve, reject) => {
          resolversRef.current.set(templateId, resolve);
          rejectersRef.current.set(templateId, reject);
          uploadFile(templateId, file, uploadPath, uploadToken);
        });

        setStatus('completing');
        await triggerUploadComplete({ templateType, templateId }).unwrap();

        setStatus('success');
        onUploaded?.();
      } catch (err) {
        console.error('Template upload failed:', err);
        setError('Template upload failed.');
        setStatus('error');
        throw err;
      }
    },
    [triggerGetUpload, triggerUploadComplete, uploadFile, onUploaded],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
  }, []);

  return { uploadTemplate, status, error, reset };
};
